import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import PriModal from "../PriModal";
import TeacherSalaryService from "../../../services/TeacherSalaryService";
import "../../../styles/TeacherSalary.css";

function getErrorMessage(error, t) {
  const responseData = error?.response?.data;

  if (typeof responseData === "string" && responseData.trim()) {
    return responseData.trim();
  }
  if (typeof responseData?.message === "string" && responseData.message.trim()) {
    return responseData.message.trim();
  }
  if (typeof responseData?.error === "string" && responseData.error.trim()) {
    return responseData.error.trim();
  }
  if (typeof error?.message === "string" && error.message.trim()) {
    return error.message.trim();
  }
  return t("teacherAttendanceRequestFailed");
}

function getCurrentMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

function getTeacherName(teacher, t) {
  if (typeof teacher?.name === "string" && teacher.name.trim()) {
    return teacher.name.trim();
  }
  return teacher?.id != null ? `${t("teacher")} #${teacher.id}` : t("teacher");
}

function formatCurrency(value, language = "en") {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return null;

  return new Intl.NumberFormat(language.startsWith("hi") ? "hi-IN" : "en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(amount);
}

function getSalaryDetails(response) {
  if (!response || typeof response !== "object") {
    return {};
  }

  const candidates = [response, response.data, response.result, response.salaryDetails]
    .filter((item) => item && typeof item === "object" && !Array.isArray(item));

  return candidates.find((item) =>
    ["payableAmount", "monthlySalary", "perDaySalary", "presentDays", "totalWorkingDays"]
      .some((key) => item[key] !== undefined && item[key] !== null)
  ) || candidates[0] || {};
}

function findSalaryAmount(response) {
  if (typeof response === "number" && Number.isFinite(response)) {
    return response;
  }

  if (typeof response === "string" && response.trim() && Number.isFinite(Number(response))) {
    return Number(response);
  }

  if (!response || typeof response !== "object") {
    return null;
  }

  const salaryDetails = getSalaryDetails(response);
  const amountKeys = [
    "payableAmount",
    "calculatedSalary",
    "payableSalary",
    "salaryAmount",
    "netSalary",
    "totalSalary",
    "amount",
    "salary",
  ];

  for (const key of amountKeys) {
    const value = salaryDetails[key];
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string" && value.trim() && Number.isFinite(Number(value))) {
      return Number(value);
    }
  }

  return null;
}

function getNumericResponseValue(value) {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() && Number.isFinite(Number(value))) {
    return Number(value);
  }
  return null;
}

function getResponseMessage(response) {
  if (typeof response === "string" && response.trim() && !Number.isFinite(Number(response))) {
    return response.trim();
  }

  if (response && typeof response === "object") {
    const message = response.message || response.statusMessage;
    if (typeof message === "string" && message.trim()) return message.trim();
  }

  return "";
}

function getAcademicYearLabel(value, t) {
  if (typeof value === "string" && value.trim() && value !== "N/A") {
    return value.trim();
  }
  if (value && typeof value === "object") {
    const year = value.year || value.currentYear;
    if (typeof year === "string" && year.trim()) return year.trim();
  }
  return t("teacherAttendanceCurrentAcademicYear");
}

const REPORT_ARRAY_KEYS = [
  "data",
  "result",
  "content",
  "records",
  "items",
  "salaryHistory",
  "salaryReport",
  "salaryRecords",
  "salaryReports",
  "history",
  "reports",
];

const REPORT_FIELD_ORDER = [
  "teacherName",
  "month",
  "monthlySalary",
  "presentDays",
  "totalWorkingDays",
  "perDaySalary",
  "payableAmount",
  "paymentDate",
  "status",
];

const REPORT_FIELD_LABELS = {
  teacherName: "teacher",
  teacherId: "teacherId",
  month: "month",
  monthlySalary: "monthlySalary",
  presentDays: "presentDays",
  totalWorkingDays: "workingDaysTotal",
  perDaySalary: "perDaySalary",
  payableAmount: "payableAmount",
  paymentDate: "paymentDate",
  schoolId: "schoolId",
  academicYearId: "academicYear",
};

function getReportRows(response) {
  if (Array.isArray(response)) return response;
  if (!response || typeof response !== "object") return [];

  for (const key of REPORT_ARRAY_KEYS) {
    if (Array.isArray(response[key])) return response[key];
  }

  for (const key of ["data", "result"]) {
    const nested = response[key];
    if (nested && typeof nested === "object") {
      const rows = getReportRows(nested);
      if (rows.length) return rows;
    }
  }

  const hasReportFields = Object.keys(response).some((key) =>
    REPORT_FIELD_ORDER.includes(key) || key === "teacherId"
  );

  return hasReportFields ? [response] : [];
}

function getReportColumns(rows) {
  const keys = [...new Set(rows.flatMap((row) =>
    row && typeof row === "object" && !Array.isArray(row) ? Object.keys(row) : []
  ))].filter((key) => key !== "schoolId" && key !== "academicYearId");

  return keys.sort((first, second) => {
    const firstIndex = REPORT_FIELD_ORDER.indexOf(first);
    const secondIndex = REPORT_FIELD_ORDER.indexOf(second);
    if (firstIndex < 0 && secondIndex < 0) return first.localeCompare(second);
    if (firstIndex < 0) return 1;
    if (secondIndex < 0) return -1;
    return firstIndex - secondIndex;
  });
}

function getReportFieldLabel(key, t) {
  if (REPORT_FIELD_LABELS[key]) return t(REPORT_FIELD_LABELS[key]);
  return key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/^./, (letter) => letter.toUpperCase());
}

function formatReportValue(value, key, t, language) {
  if (value === null || value === undefined || value === "") return "—";

  if (key === "teacher" && typeof value === "object") {
    return value.name || value.teacherName || (value.id ? `${t("teacher")} #${value.id}` : "—");
  }

  if (typeof value === "boolean") return value ? t("yes") : t("no");

  if (typeof value === "object") {
    return value.name || value.label || value.year || value.month || "—";
  }

  if (
    typeof value === "number" &&
    /salary|amount|payment|payable/i.test(key)
  ) {
    return formatCurrency(value, language);
  }

  return String(value);
}

export default function TeacherSalary({
  schoolId,
  schoolName,
  academicYearId,
  academicYearName,
  teachers = [],
  onToast,
}) {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage || i18n.language || "en";
  const [teacherId, setTeacherId] = useState("");
  const [month, setMonth] = useState(getCurrentMonth);
  const [calculation, setCalculation] = useState(null);
  const [calculationError, setCalculationError] = useState("");
  const [calculating, setCalculating] = useState(false);
  const [confirmPay, setConfirmPay] = useState(false);
  const [paying, setPaying] = useState(false);
  const [paid, setPaid] = useState(false);
  const [activeView, setActiveView] = useState("calculate");
  const [reportType, setReportType] = useState("school");
  const [reportTeacherId, setReportTeacherId] = useState("");
  const [reportMonth, setReportMonth] = useState("");
  const [reportRows, setReportRows] = useState([]);
  const [reportError, setReportError] = useState("");
  const [reportLoading, setReportLoading] = useState(false);
  const onToastRef = useRef(onToast);

  useEffect(() => {
    onToastRef.current = onToast;
  }, [onToast]);

  const availableTeachers = useMemo(
    () => (Array.isArray(teachers) ? teachers.filter((teacher) => teacher?.id != null) : []),
    [teachers]
  );
  const selectedTeacher = availableTeachers.find(
    (teacher) => String(teacher.id) === String(teacherId)
  );
  const schoolReady = schoolId !== undefined && schoolId !== null && schoolId !== "";
  const academicYearReady =
    academicYearId !== undefined && academicYearId !== null && academicYearId !== "";
  const academicYearLabel = getAcademicYearLabel(academicYearName, t);
  const salaryDetails = getSalaryDetails(calculation);
  const salaryAmount = findSalaryAmount(calculation);
  const salaryAmountLabel = getNumericResponseValue(salaryDetails.payableAmount) !== null
    ? t("payableAmount")
    : getNumericResponseValue(salaryDetails.monthlySalary) !== null
      ? t("monthlySalary")
      : t("calculatedSalary");
  const responseMessage = getResponseMessage(calculation);
  const resultTeacherName =
    typeof salaryDetails.teacherName === "string" && salaryDetails.teacherName.trim()
      ? salaryDetails.teacherName.trim()
      : getTeacherName(selectedTeacher, t);
  const resultMonth =
    typeof salaryDetails.month === "string" && salaryDetails.month.trim()
      ? salaryDetails.month.trim()
      : month;
  const reportTeacher = availableTeachers.find(
    (teacher) => String(teacher.id) === String(reportTeacherId)
  );
  const reportColumns = getReportColumns(reportRows);

  const notify = (message, type) => onToastRef.current?.(message, type);

  const updateInput = (setter) => (event) => {
    setter(event.target.value);
    setCalculation(null);
    setCalculationError("");
    setPaid(false);
  };

  const validate = () => {
    if (!schoolReady || !academicYearReady) {
      return t("teacherSalaryAccountNotConfigured");
    }
    if (!teacherId) return t("teacherAttendanceSelectTeacher");
    if (!month) return t("teacherSalarySelectMonth");
    return "";
  };

  const buildRequest = () => ({
    teacherId,
    schoolId,
    academicYearId,
    month,
  });

  const calculateSalary = async (event) => {
    event.preventDefault();
    const validationError = validate();
    if (validationError) {
      setCalculationError(validationError);
      return;
    }

    setCalculating(true);
    setCalculationError("");
    setCalculation(null);
    setPaid(false);

    try {
      const response = await TeacherSalaryService.calculate(buildRequest());
      setCalculation(response);
    } catch (error) {
      const message = getErrorMessage(error, t);
      setCalculationError(message);
      notify(message, "error");
    } finally {
      setCalculating(false);
    }
  };

  const paySalary = async () => {
    const validationError = validate();
    if (validationError || !calculation) {
      setCalculationError(validationError || t("teacherSalaryCalculateBeforePayment"));
      setConfirmPay(false);
      return;
    }

    setPaying(true);
    setCalculationError("");
    try {
      const calculatedWorkingDays = getNumericResponseValue(salaryDetails.totalWorkingDays);
      await TeacherSalaryService.pay({
        ...buildRequest(),
        ...(calculatedWorkingDays !== null ? { workingDays: calculatedWorkingDays } : {}),
      });
      setPaid(true);
      setConfirmPay(false);
      notify(t("teacherSalaryPaymentRecordedSuccess"), "success");
    } catch (error) {
      const message = getErrorMessage(error, t);
      setCalculationError(message);
      notify(message, "error");
    } finally {
      setPaying(false);
    }
  };

  const loadReport = async (event) => {
    event.preventDefault();

    if (!schoolReady || !academicYearReady) {
      setReportError(t("teacherSalaryReportsNotConfigured"));
      return;
    }
    if (reportType === "teacher" && !reportTeacherId) {
      setReportError(t("teacherSalarySelectTeacherFirst"));
      return;
    }

    setReportLoading(true);
    setReportError("");
    setReportRows([]);

    try {
      const response = reportType === "school"
        ? await TeacherSalaryService.getSchoolReport({
          schoolId,
          academicYearId,
          ...(reportMonth ? { month: reportMonth } : {}),
        })
        : await TeacherSalaryService.getTeacherHistory({
          teacherId: reportTeacherId,
          schoolId,
          academicYearId,
          ...(reportMonth ? { month: reportMonth } : {}),
        });

      setReportRows(getReportRows(response));
    } catch (error) {
      const message = getErrorMessage(error, t);
      setReportError(message);
      notify(message, "error");
    } finally {
      setReportLoading(false);
    }
  };

  const clearReportFilters = () => {
    setReportTeacherId("");
    setReportMonth("");
    setReportRows([]);
    setReportError("");
  };

  return (
    <div className="teacher-salary-page">
      <header className="teacher-salary-header">
        <div>
          <div className="teacher-salary-eyebrow">{t("teacherLms")}</div>
          <h1>{t("teacherSalaryTitle")}</h1>
          <p>{t("teacherSalaryDescription")}</p>
        </div>
      </header>

      <nav className="teacher-salary-tabs" aria-label={t("teacherSalaryTools")}>
        <button
          type="button"
          className={activeView === "calculate" ? "active" : ""}
          onClick={() => setActiveView("calculate")}
        >
          {t("teacherSalaryCalculation")}
        </button>
        <button
          type="button"
          className={activeView === "reports" ? "active" : ""}
          onClick={() => setActiveView("reports")}
        >
          {t("teacherSalaryReports")}
        </button>
      </nav>

      {activeView === "reports" ? (
        <>
          {(!schoolReady || !academicYearReady) && (
            <div className="teacher-salary-alert" role="alert">
              {t("teacherSalaryReportsNotConfigured")}
            </div>
          )}

          <section className="teacher-salary-card">
            <div className="teacher-salary-section-heading">
              <div>
                <h2>{t("teacherSalaryReports")}</h2>
                <p>{t("teacherSalaryReportsDescription")}</p>
              </div>
              <div className="teacher-salary-year-chip">
                <span>{t("school")}</span>
                <strong>{schoolName || `${t("school")} #${schoolId}`}</strong>
                <span>{t("academicYear")}</span>
                <strong>{academicYearLabel}</strong>
              </div>
            </div>

            <form onSubmit={loadReport}>
              <div className="teacher-salary-report-switch" role="group" aria-label={t("teacherSalaryReportType")}>
                <button
                  type="button"
                  className={reportType === "school" ? "active" : ""}
                  onClick={() => {
                    setReportType("school");
                    setReportRows([]);
                    setReportError("");
                  }}
                >
                  {t("teacherSalarySchoolReport")}
                </button>
                <button
                  type="button"
                  className={reportType === "teacher" ? "active" : ""}
                  onClick={() => {
                    setReportType("teacher");
                    setReportRows([]);
                    setReportError("");
                  }}
                >
                  {t("teacherSalaryTeacherHistory")}
                </button>
              </div>

              <div className="teacher-salary-report-filters">
                {reportType === "teacher" && (
                  <div className="teacher-salary-field">
                    <label htmlFor="teacher-salary-report-teacher">{t("teacher")} <span>*</span></label>
                    <select
                      id="teacher-salary-report-teacher"
                      value={reportTeacherId}
                      onChange={(event) => {
                        setReportTeacherId(event.target.value);
                        setReportRows([]);
                        setReportError("");
                      }}
                      disabled={reportLoading}
                      required
                    >
                      <option value="">{t("teacherSalarySelectTeacher")}</option>
                      {availableTeachers.map((teacher) => (
                        <option key={teacher.id} value={teacher.id}>
                          {getTeacherName(teacher, t)}
                        </option>
                      ))}
                    </select>
                    {availableTeachers.length === 0 && (
                      <small>{t("teacherSalaryNoTeachers")}</small>
                    )}
                  </div>
                )}

                <div className="teacher-salary-field">
                  <label htmlFor="teacher-salary-report-month">{t("month")} <span className="optional-label">{t("teacherSalaryOptionalAllMonths")}</span></label>
                  <input
                    id="teacher-salary-report-month"
                    type="month"
                    value={reportMonth}
                    onChange={(event) => {
                      setReportMonth(event.target.value);
                      setReportRows([]);
                      setReportError("");
                    }}
                    disabled={reportLoading}
                  />
                </div>
              </div>

              {reportError && (
                <div className="teacher-salary-alert teacher-salary-form-error" role="alert">
                  {reportError}
                </div>
              )}

              <div className="teacher-salary-report-actions">
                <button
                  className="pri-btn pri-btn-primary"
                  type="submit"
                  disabled={reportLoading || !schoolReady || !academicYearReady}
                >
                  {reportLoading ? t("teacherSalaryLoadingReport") : t("teacherSalaryViewReport")}
                </button>
                <button
                  className="pri-btn pri-btn-ghost"
                  type="button"
                  onClick={clearReportFilters}
                  disabled={reportLoading}
                >
                  {t("teacherAttendanceClearFilters")}
                </button>
              </div>
            </form>
          </section>

          <section className="teacher-salary-card teacher-salary-report-results">
            <div className="teacher-salary-report-results-heading">
              <div>
                <h2>{reportType === "school" ? t("teacherSalarySchoolReportTitle") : t("teacherSalaryTeacherHistory")}</h2>
                <p>
                  {reportType === "school"
                    ? t("teacherSalaryCurrentSchoolRecords")
                    : reportTeacherId
                      ? t("teacherSalaryRecordsForTeacher", { teacher: getTeacherName(reportTeacher, t) })
                      : t("teacherSalarySelectTeacherHistory")}
                </p>
              </div>
              {reportRows.length > 0 && (
                <span className="teacher-salary-report-count">
                  {t("teacherSalaryRecordCount", { count: reportRows.length })}
                </span>
              )}
            </div>

            {reportLoading ? (
              <div className="teacher-salary-report-loading">{t("teacherSalaryLoadingRecords")}</div>
            ) : reportError ? (
              <div className="teacher-salary-report-empty" role="alert">
                <strong>{t("teacherSalaryCouldNotLoadRecords")}</strong>
                <span>{reportError}</span>
              </div>
            ) : reportRows.length === 0 ? (
              <div className="teacher-salary-report-empty">
                <strong>{reportType === "teacher" && !reportTeacherId ? t("teacherSalaryChooseTeacherToBegin") : t("teacherSalaryNoRecords")}</strong>
                <span>
                  {reportType === "teacher" && !reportTeacherId
                    ? t("teacherSalarySearchSelectTeacher")
                    : t("teacherSalaryChooseFilters")}
                </span>
              </div>
            ) : reportColumns.length === 0 ? (
              <div className="teacher-salary-report-empty">
                <strong>{t("teacherSalaryReportReceived")}</strong>
                <span>{t("teacherSalaryNoTabularFields")}</span>
              </div>
            ) : (
              <div className="teacher-salary-report-table-wrap">
                <table className="teacher-salary-report-table">
                  <thead>
                    <tr>
                      {reportColumns.map((column) => (
                        <th key={column}>{getReportFieldLabel(column, t)}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {reportRows.map((row, index) => (
                      <tr key={row?.id ?? `${row?.teacherId ?? "record"}-${row?.month ?? index}-${index}`}>
                        {reportColumns.map((column) => (
                          <td key={column}>{formatReportValue(row?.[column], column, t, language)}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      ) : (
      <>
      {(!schoolReady || !academicYearReady) && (
        <div className="teacher-salary-alert" role="alert">
          {t("teacherSalaryAccountNotConfigured")}
        </div>
      )}

      <section className="teacher-salary-card">
        <div className="teacher-salary-section-heading">
          <div>
            <h2>{t("teacherSalaryDetails")}</h2>
            <p>{t("teacherSalaryCalculationHelp")}</p>
          </div>
          <div className="teacher-salary-year-chip">
            <span>{t("school")}</span>
            <strong>{schoolName || `${t("school")} #${schoolId}`}</strong>
            <span>{t("academicYear")}</span>
            <strong>{academicYearLabel}</strong>
          </div>
        </div>

        <form onSubmit={calculateSalary}>
          <div className="teacher-salary-form-grid">
            <div className="teacher-salary-field">
              <label htmlFor="teacher-salary-teacher">{t("teacher")} <span>*</span></label>
              <select
                id="teacher-salary-teacher"
                value={teacherId}
                onChange={updateInput(setTeacherId)}
                disabled={calculating || paying || availableTeachers.length === 0}
                required
              >
                <option value="">{t("teacherSalarySelectTeacher")}</option>
                {availableTeachers.map((teacher) => (
                  <option key={teacher.id} value={teacher.id}>
                    {getTeacherName(teacher, t)}
                  </option>
                ))}
              </select>
              {availableTeachers.length === 0 && (
                <small>{t("teacherSalaryNoTeachers")}</small>
              )}
            </div>

            <div className="teacher-salary-field">
              <label htmlFor="teacher-salary-month">{t("teacherSalaryMonth")} <span>*</span></label>
              <input
                id="teacher-salary-month"
                type="month"
                value={month}
                onChange={updateInput(setMonth)}
                disabled={calculating || paying}
                required
              />
            </div>

          </div>

          {calculationError && (
            <div className="teacher-salary-alert teacher-salary-form-error" role="alert">
              {calculationError}
            </div>
          )}

          <div className="teacher-salary-form-actions">
            <button
              className="pri-btn pri-btn-primary"
              type="submit"
              disabled={calculating || paying || !schoolReady || !academicYearReady}
            >
              {calculating ? t("teacherSalaryCalculating") : t("teacherSalaryCalculate")}
            </button>
          </div>
        </form>
      </section>

      {calculation && (
        <section className="teacher-salary-result" aria-live="polite">
          <div className="teacher-salary-result-main">
            <div className="teacher-salary-result-icon" aria-hidden="true">₹</div>
            <div>
              <span className="teacher-salary-result-label">{salaryAmountLabel}</span>
              <strong className="teacher-salary-amount">
                {salaryAmount === null ? t("teacherSalaryCalculationComplete") : formatCurrency(salaryAmount, language)}
              </strong>
              {responseMessage && (
                <p className="teacher-salary-result-message">{responseMessage}</p>
              )}
            </div>
          </div>
          <div className="teacher-salary-result-details">
            <div><span>{t("teacher")}</span><strong>{resultTeacherName}</strong></div>
            <div><span>{t("month")}</span><strong>{resultMonth}</strong></div>
            {getNumericResponseValue(salaryDetails.presentDays) !== null && (
              <div><span>{t("presentDays")}</span><strong>{salaryDetails.presentDays}</strong></div>
            )}
            {getNumericResponseValue(salaryDetails.totalWorkingDays) !== null && (
              <div><span>{t("workingDaysTotal")}</span><strong>{salaryDetails.totalWorkingDays}</strong></div>
            )}
            {getNumericResponseValue(salaryDetails.monthlySalary) !== null && (
              <div><span>{t("monthlySalary")}</span><strong>{formatCurrency(salaryDetails.monthlySalary, language)}</strong></div>
            )}
            {getNumericResponseValue(salaryDetails.perDaySalary) !== null && (
              <div><span>{t("perDaySalary")}</span><strong>{formatCurrency(salaryDetails.perDaySalary, language)}</strong></div>
            )}
            {getNumericResponseValue(salaryDetails.payableAmount) !== null && (
              <div className="teacher-salary-payable-detail">
                <span>{t("payableAmount")}</span>
                <strong>{formatCurrency(salaryDetails.payableAmount, language)}</strong>
              </div>
            )}
            <div>
              <span>{t("teacherSalaryPaymentStatus")}</span>
              <strong className={paid ? "teacher-salary-paid" : "teacher-salary-pending"}>
                {paid ? t("paid") : t("teacherSalaryNotPaid")}
              </strong>
            </div>
          </div>
          <div className="teacher-salary-pay-action">
            <button
              className={`pri-btn ${paid ? "pri-btn-ghost" : "pri-btn-primary"}`}
              type="button"
              onClick={() => setConfirmPay(true)}
              disabled={paid || paying}
            >
              {paid ? t("teacherSalaryPaymentRecorded") : t("teacherSalaryPay")}
            </button>
          </div>
        </section>
      )}

      {confirmPay && (
        <PriModal
          title={t("teacherSalaryConfirmPayment")}
          onClose={() => !paying && setConfirmPay(false)}
          footer={
            <div className="teacher-salary-modal-actions">
              <button
                className="pri-btn pri-btn-ghost"
                type="button"
                onClick={() => setConfirmPay(false)}
                disabled={paying}
              >
                {t("cancel")}
              </button>
              <button
                className="pri-btn pri-btn-primary"
                type="button"
                onClick={paySalary}
                disabled={paying}
              >
                {paying ? t("teacherSalaryProcessing") : t("teacherSalaryConfirmPayment")}
              </button>
            </div>
          }
        >
          <div className="teacher-salary-confirm-content">
            <p>{t("teacherSalaryConfirmPaymentMessage")}</p>
            <dl>
              <div><dt>{t("teacher")}</dt><dd>{getTeacherName(selectedTeacher, t)}</dd></div>
              <div><dt>{t("month")}</dt><dd>{resultMonth}</dd></div>
              {salaryAmount !== null && (
                <div><dt>{t("calculatedSalary")}</dt><dd>{formatCurrency(salaryAmount, language)}</dd></div>
              )}
            </dl>
          </div>
        </PriModal>
      )}
      </>)
      }
    </div>
  );
}
